import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ChevronRight, Users, Target, Heart, TrendingUp, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AppLink from '../components/AppLink';
import { heroSlides, partyInfo } from '../mockData';
import { DriveSection } from '../components/DriveSection';

const Home = () => {
  const { language, t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [leaders, setLeaders] = useState([]);
  const [news, setNews] = useState([]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadersRes, newsRes] = await Promise.all([
          fetch('/api/leaders/'),
          fetch('/api/news/?limit=3'),
        ]);
        if (leadersRes.ok) setLeaders(await leadersRes.json());
        if (newsRes.ok) setNews(await newsRes.json());
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, []);

  const stats = [
    { icon: Users, value: '500,000+', label: { en: 'Active Members', bn: 'সক্রিয় সদস্য' } },
    { icon: Target, value: '64', label: { en: 'Districts Covered', bn: 'জেলায় কার্যক্রম' } },
    { icon: Heart, value: '200+', label: { en: 'Community Projects', bn: 'সামাজিক প্রকল্প' } },
    { icon: TrendingUp, value: '95%', label: { en: 'Youth Engagement', bn: 'যুব সংযুক্তি' } },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Carousel */}
      <section className="relative h-[340px] md:h-[420px] overflow-hidden bg-gray-800" data-testid="hero-carousel">
        {heroSlides.map((slide, idx) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 pointer-events-none'}`}
          >
            {slide.image && <img src={slide.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 max-w-3xl">
                  {t(slide.title)}
                </h1>
                <p className="text-lg text-gray-200 mb-8 max-w-2xl">{t(slide.subtitle)}</p>
                <div className="flex flex-wrap gap-4">
                  <AppLink to="/join">
                    <Button size="lg" className="bg-party-red hover:bg-party-red-600 text-white" data-testid="hero-join-btn">
                      {language === 'en' ? 'Join Our Movement' : 'আমাদের আন্দোলনে যোগ দিন'}
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </Button>
                  </AppLink>
                  <AppLink to="/about">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="hero-learn-btn">
                      {language === 'en' ? 'Learn More' : 'আরও জানুন'}
                    </Button>
                  </AppLink>
                </div>
              </div>
            </div>
          </div>
        ))}
        <button onClick={prevSlide} data-testid="hero-prev-btn" className="hidden sm:block absolute z-20 left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm p-3 rounded-full transition-colors">
          <ChevronLeft className="h-6 w-6 text-white" />
        </button>
        <button onClick={nextSlide} data-testid="hero-next-btn" className="hidden sm:block absolute z-20 right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm p-3 rounded-full transition-colors">
          <ChevronRight className="h-6 w-6 text-white" />
        </button>
        <div className="absolute z-20 bottom-6 left-1/2 -translate-x-1/2 flex space-x-2">
          {heroSlides.map((_, idx) => (
            <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-3 h-3 rounded-full transition-colors ${idx === currentSlide ? 'bg-party-red' : 'bg-white/50'}`} />
          ))}
        </div>
      </section>

      {/* Flag Color Bar */}
      <div className="flex h-2">
        <div className="flex-1 bg-party-red"></div>
        <div className="flex-1 bg-white"></div>
        <div className="flex-1 bg-party-green"></div>
      </div>

      {/* Stats Section */}
      <section className="bg-party-red py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <stat.icon className="h-10 w-10 mx-auto mb-3 opacity-80" />
                <div className="text-3xl lg:text-4xl font-bold mb-1">{stat.value}</div>
                <div className="text-red-100 text-sm">{t(stat.label)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DriveSection />

      {/* About Section with Flag */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-1 bg-party-red-100 text-party-red rounded-full text-sm font-semibold mb-4">
                {t({ en: 'About NDP', bn: 'এনডিপি সম্পর্কে' })}
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                {t({ en: 'A New Era of Democratic Politics', bn: 'গণতান্ত্রিক রাজনীতির নতুন যুগ' })}
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t({
                  en: 'Founded on integrity, inclusion, and development — NDP is a progressive political movement committed to transforming governance from the grassroots. We believe in a Bangladesh where every citizen thrives.',
                  bn: 'সততা, অন্তর্ভুক্তি ও উন্নয়নের উপর প্রতিষ্ঠিত — এনডিপি একটি অগ্রগতিমুখী রাজনৈতিক আন্দোলন যা তৃণমূল স্তর থেকে শাসনকে পরিবর্তন করতে প্রতিশ্রুতিবদ্ধ।',
                })}
              </p>
              <AppLink to="/about">
                <Button className="bg-party-green hover:bg-party-green-600 text-white" data-testid="about-cta-btn">
                  {t({ en: 'Read More About Us', bn: 'আমাদের সম্পর্কে আরও পড়ুন' })}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </AppLink>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img
                  src={partyInfo.flagImage}
                  alt="NDP Party Flag"
                  className="w-80 h-80 object-contain rounded-2xl shadow-2xl"
                  data-testid="party-flag-image"
                />
                <div className="absolute -bottom-4 -right-4 w-80 h-80 border-4 border-party-green/30 rounded-2xl -z-10"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leaders Section */}
      {leaders.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-1 bg-party-green-100 text-party-green rounded-full text-sm font-semibold mb-4">
                {t({ en: 'Leadership', bn: 'নেতৃত্ব' })}
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                {t({ en: 'Meet Our Leaders', bn: 'আমাদের নেতাদের সাথে পরিচিত হন' })}
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {(() => {
                const national = leaders.filter(l => l.level === 'national');
                const chairman = national.find(l => l.order === 1);
                const noorJahan = national.find(l => (l.name?.en || '').includes('Noor Jahan'));
                const genSec = national.find(l => l.order === 2);
                const fouzia = national.find(l => (l.name?.en || '').includes('Fouzia'));
                return [chairman, noorJahan, genSec, fouzia].filter(Boolean).map((leader) => (
                <Card key={leader._id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-t-4 border-t-party-red">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={leader.image_url || leader.image} alt={t(leader.name)} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900">{t(leader.name)}</h3>
                    <p className="text-party-red font-semibold mt-1">{t(leader.designation || leader.position)}</p>
                  </CardContent>
                </Card>
                ));
              })()}
            </div>
            <div className="text-center mt-10">
              <AppLink to="/leadership">
                <Button variant="outline" className="border-party-red text-party-red hover:bg-party-red hover:text-white" data-testid="view-leaders-btn">
                  {t({ en: 'View All Leaders', bn: 'সব নেতা দেখুন' })}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </AppLink>
            </div>
          </div>
        </section>
      )}

      {/* News Section */}
      {news.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-1 bg-party-red-100 text-party-red rounded-full text-sm font-semibold mb-4">
                {t({ en: 'Latest News', bn: 'সর্বশেষ সংবাদ' })}
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                {t({ en: 'Stay Updated', bn: 'আপডেট থাকুন' })}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {news.slice(0, 3).map((article) => (
                <Card key={article._id} className="overflow-hidden hover:shadow-2xl transition-all duration-300">
                  <div className="aspect-video overflow-hidden">
                    <img src={article.image_url || article.image} alt={t(article.title)} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-6">
                    <div className="text-sm text-gray-500 mb-2">
                      {new Date(article.date || article.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'bn-BD')}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{t(article.title)}</h3>
                    <p className="text-gray-600 text-sm line-clamp-3">{t(article.excerpt || article.content)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center mt-10">
              <AppLink to="/news">
                <Button variant="outline" className="border-party-green text-party-green hover:bg-party-green hover:text-white" data-testid="view-news-btn">
                  {t({ en: 'View All News', bn: 'সব সংবাদ দেখুন' })}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </AppLink>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-party-red via-party-red-600 to-party-red-700"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-party-green rounded-full translate-y-1/2 -translate-x-1/2"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            {t({ en: 'Join the Movement for a Better Bangladesh', bn: 'একটি উন্নত বাংলাদেশের আন্দোলনে যোগ দিন' })}
          </h2>
          <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
            {t({
              en: 'Together, we can build a democratic, inclusive, and prosperous nation. Your voice matters.',
              bn: 'একসাথে আমরা একটি গণতান্ত্রিক, অন্তর্ভুক্তিমূলক এবং সমৃদ্ধ জাতি গড়তে পারি। আপনার কণ্ঠস্বর গুরুত্বপূর্ণ।',
            })}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <AppLink to="/join">
              <Button size="lg" className="bg-white text-party-red hover:bg-gray-100" data-testid="cta-join-btn">
                {t({ en: 'Become a Member', bn: 'সদস্য হন' })}
              </Button>
            </AppLink>
            <AppLink to="/donate">
              <Button size="lg" className="bg-party-green hover:bg-party-green-600 text-white" data-testid="cta-donate-btn">
                {t({ en: 'Support Us', bn: 'আমাদের সহায়তা করুন' })}
              </Button>
            </AppLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
